<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import type { Invoice } from '~/types/models';
import { formatMoney, formatShortDate } from '~/lib/formatters';

const props = defineProps<{
    invoice: Invoice;
    projectName?: string;
    currency?: string;
}>();

function statusVariant(status: Invoice['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
    if (status === 'paid') return 'default';
    if (status === 'overdue') return 'destructive';
    if (status === 'draft') return 'outline';
    return 'secondary';
}

function lifecycleBadgeLabel(invoice: Invoice): string {
    if (invoice.status === 'paid') return 'Settled';
    if (invoice.status === 'partial') return 'Partially paid';
    if (invoice.status === 'overdue') return 'Needs follow-up';
    if (invoice.status === 'sent') return 'Awaiting payment';
    return 'Draft';
}
</script>

<template>
    <div class="rounded-xl border border-border/70 bg-background/76 px-3 py-3">
        <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
                <p class="text-sm font-medium text-foreground">{{ invoice.title || invoice.invoiceNumber || 'Invoice' }}</p>
                <p class="mt-1 text-xs text-muted-foreground">
                    {{ projectName || 'Unlinked invoice' }}
                    <span v-if="invoice.invoiceNumber"> • {{ invoice.invoiceNumber }}</span>
                </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
                <Badge :variant="statusVariant(invoice.status)">{{ invoice.status }}</Badge>
                <Badge :variant="statusVariant(invoice.status)">{{ lifecycleBadgeLabel(invoice) }}</Badge>
            </div>
        </div>

        <div class="mt-3 grid gap-2 sm:grid-cols-3">
            <div class="rounded-lg border border-border/60 bg-background/80 px-3 py-2">
                <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Outstanding</p>
                <p class="mt-1 text-sm font-medium text-foreground">{{ formatMoney(invoice.amountDue || 0, invoice.currency || currency || 'GBP') }}</p>
            </div>
            <div class="rounded-lg border border-border/60 bg-background/80 px-3 py-2">
                <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Issued</p>
                <p class="mt-1 text-sm text-foreground">{{ formatShortDate(invoice.issueDate, 'Not issued') }}</p>
            </div>
            <div class="rounded-lg border border-border/60 bg-background/80 px-3 py-2">
                <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Due</p>
                <p class="mt-1 text-sm text-foreground">{{ formatShortDate(invoice.dueDate, 'No due date') }}</p>
            </div>
        </div>
    </div>
</template>
